import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Flex, Heading, Table, Thead, Tbody, Tr, Th, Td,
  Badge, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  Input, Select, Switch, useDisclosure, useToast, Spinner, Text,
  HStack, Tooltip, VStack,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiLink, FiStopCircle, FiRefreshCw, FiLogIn, FiEdit2, FiVideo, FiRadio } from 'react-icons/fi';

const DEFAULT_FORM = {
  name: '', room_type: 'meeting', max_participants: 100,
  chat_enabled: true, screenshare_enabled: true,
  recording_enabled: false, streaming_enabled: false,
  rtmp_url: '', rtmp_stream_key: '',
};

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingRoom, setEditingRoom] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [joinTokenRoom, setJoinTokenRoom] = useState(null);
  const [generatedToken, setGeneratedToken] = useState(null);
  const { isOpen: isTokenOpen, onOpen: onTokenOpen, onClose: onTokenClose } = useDisclosure();
  const toast = useToast();

  const token = localStorage.getItem('meet_token');
  const headers = { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' };

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/rooms/', { headers });
      if (res.ok) setRooms(await res.json());
    } catch (e) {
      toast({ title: 'Failed to load rooms', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const openCreateRoom = () => {
    setEditingRoom(null);
    setForm(DEFAULT_FORM);
    onOpen();
  };

  const openEditRoom = (room) => {
    setEditingRoom(room);
    setForm({
      name: room.name, room_type: room.room_type, max_participants: room.max_participants,
      chat_enabled: room.chat_enabled, screenshare_enabled: room.screenshare_enabled,
      recording_enabled: room.recording_enabled, streaming_enabled: room.streaming_enabled,
      rtmp_url: room.rtmp_url || '', rtmp_stream_key: room.rtmp_stream_key || '',
    });
    onOpen();
  };

  const saveRoom = async () => {
    const method = editingRoom ? 'PATCH' : 'POST';
    const url = editingRoom ? `/api/v1/rooms/${editingRoom.id}/` : '/api/v1/rooms/';
    try {
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (res.ok) {
        toast({ title: editingRoom ? 'Room updated' : 'Room created', status: 'success', duration: 2000 });
        onClose();
        setForm(DEFAULT_FORM);
        setEditingRoom(null);
        fetchRooms();
      } else {
        toast({ title: editingRoom ? 'Failed to update room' : 'Failed to create room', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm('Delete this room?')) return;
    try {
      const res = await fetch(`/api/v1/rooms/${id}/`, { method: 'DELETE', headers });
      if (res.status === 204) {
        toast({ title: 'Room deleted', status: 'success', duration: 2000 });
        fetchRooms();
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const endSession = async (id) => {
    try {
      const res = await fetch(`/api/v1/rooms/${id}/end_session/`, { method: 'POST', headers });
      if (res.ok) {
        toast({ title: 'Session ended', status: 'success', duration: 2000 });
        fetchRooms();
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const generateJoinLink = (room) => {
    // No token baked in on purpose: this link is meant to be shared with and
    // reused by any number of attendees. Each person who opens it hits
    // JoinRoom.js with no ?token=, so it mints them their own fresh
    // token/user_id there — baking one identity in here would mean everyone
    // who opens the link joins as the same participant and can't see each
    // other (they'd each be filtered out of their own roster as "themselves").
    setGeneratedToken(`${window.location.origin}/join/${room.room_id}`);
    setJoinTokenRoom(room);
    onTokenOpen();
  };

  const joinAsHost = async (room) => {
    // The only place a 'host' role token gets minted from the UI — everyone
    // else comes in via the token-less shareable link above, which
    // self-join-token always issues as 'attendee'. This reuses the existing,
    // already-authenticated /api/v1/join-token endpoint (no backend change
    // needed) rather than the public self-join path.
    try {
      const res = await fetch('/api/v1/join-token', {
        method: 'POST', headers,
        body: JSON.stringify({ room_id: room.room_id, role: 'host', display_name: 'Host' }),
      });
      if (res.ok) {
        const data = await res.json();
        window.open(`${window.location.origin}/room?roomId=${room.room_id}&token=${data.token}&displayName=Host`, '_blank');
      } else {
        toast({ title: 'Failed to join as host', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error joining as host', status: 'error', duration: 3000 });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.700">Rooms</Heading>
        <HStack>
          <IconButton icon={<FiRefreshCw />} onClick={fetchRooms} variant="ghost" aria-label="Refresh" />
          <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openCreateRoom}>New Room</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={12}><Spinner size="lg" color="teal.500" /></Flex>
      ) : (
        <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Room ID</Th>
                <Th>Type</Th>
                <Th>Participants</Th>
                <Th>Status</Th>
                <Th>Features</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rooms.map((room) => (
                <Tr key={room.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="500">{room.name}</Td>
                  <Td fontFamily="mono" fontSize="xs" color="gray.500">{room.room_id}</Td>
                  <Td>
                    <Badge colorScheme={room.room_type === 'webinar' ? 'purple' : 'teal'}>
                      {room.room_type}
                    </Badge>
                  </Td>
                  <Td>{room.participant_count} / {room.max_participants}</Td>
                  <Td>
                    <Badge colorScheme={room.is_live ? 'green' : 'gray'}>
                      {room.is_live ? 'Live' : 'Idle'}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      {room.chat_enabled && <Badge colorScheme="blue" fontSize="xs">Chat</Badge>}
                      {room.screenshare_enabled && <Badge colorScheme="orange" fontSize="xs">Screen</Badge>}
                      {room.recording_enabled && <Badge colorScheme="red" fontSize="xs">Recording</Badge>}
                      {room.streaming_enabled && <Badge colorScheme="pink" fontSize="xs">Streaming</Badge>}
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="Edit room">
                        <IconButton size="xs" icon={<FiEdit2 />} variant="ghost" colorScheme="gray"
                          onClick={() => openEditRoom(room)} aria-label="Edit room" />
                      </Tooltip>
                      <Tooltip label="Join as host">
                        <IconButton size="xs" icon={<FiLogIn />} variant="ghost" colorScheme="purple"
                          onClick={() => joinAsHost(room)} aria-label="Join as host" />
                      </Tooltip>
                      <Tooltip label="Generate join link">
                        <IconButton size="xs" icon={<FiLink />} variant="ghost" colorScheme="teal"
                          onClick={() => generateJoinLink(room)} aria-label="Join link" />
                      </Tooltip>
                      {room.is_live && (
                        <Tooltip label="End session">
                          <IconButton size="xs" icon={<FiStopCircle />} variant="ghost" colorScheme="orange"
                            onClick={() => endSession(room.id)} aria-label="End session" />
                        </Tooltip>
                      )}
                      <Tooltip label="Delete room">
                        <IconButton size="xs" icon={<FiTrash2 />} variant="ghost" colorScheme="red"
                          onClick={() => deleteRoom(room.id)} aria-label="Delete" />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {rooms.length === 0 && (
                <Tr><Td colSpan={7} textAlign="center" py={8} color="gray.400">No rooms yet</Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create/Edit Room Modal */}
      <Modal isOpen={isOpen} onClose={() => { onClose(); setEditingRoom(null); }} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingRoom ? `Edit Room — ${editingRoom.name}` : 'Create Room'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Room Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  focusBorderColor="teal.400" />
              </FormControl>
              <FormControl>
                <FormLabel>Room Type</FormLabel>
                <Select value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })}>
                  <option value="meeting">Meeting</option>
                  <option value="webinar">Webinar</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Max Participants</FormLabel>
                <Input type="number" value={form.max_participants}
                  onChange={(e) => setForm({ ...form, max_participants: parseInt(e.target.value) })}
                  focusBorderColor="teal.400" />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Chat Enabled</FormLabel>
                <Switch colorScheme="teal" isChecked={form.chat_enabled}
                  onChange={(e) => setForm({ ...form, chat_enabled: e.target.checked })} />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Screenshare Enabled</FormLabel>
                <Switch colorScheme="teal" isChecked={form.screenshare_enabled}
                  onChange={(e) => setForm({ ...form, screenshare_enabled: e.target.checked })} />
              </FormControl>

              <Box w="full" borderTop="1px solid" borderColor="gray.200" pt={4}>
                <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" mb={3}>
                  Recording &amp; Streaming
                </Text>
                <VStack spacing={4} align="stretch">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb={0} flex={1}>
                      <HStack><FiVideo /><Text>Recording Enabled</Text></HStack>
                    </FormLabel>
                    <Switch colorScheme="red" isChecked={form.recording_enabled}
                      onChange={(e) => setForm({ ...form, recording_enabled: e.target.checked })} />
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb={0} flex={1}>
                      <HStack><FiRadio /><Text>Streaming (RTMP) Enabled</Text></HStack>
                    </FormLabel>
                    <Switch colorScheme="pink" isChecked={form.streaming_enabled}
                      onChange={(e) => setForm({ ...form, streaming_enabled: e.target.checked })} />
                  </FormControl>
                  {form.streaming_enabled && (
                    <>
                      <Text fontSize="xs" color="gray.500" mt={-2}>
                        Optional: set a fixed destination here to lock it — the moderator won't be able to
                        change it when starting a stream. Leave blank to let the moderator enter their own
                        destination at stream time.
                      </Text>
                      <FormControl>
                        <FormLabel fontSize="sm">RTMP URL</FormLabel>
                        <Input value={form.rtmp_url} placeholder="rtmp://a.rtmp.youtube.com/live2"
                          onChange={(e) => setForm({ ...form, rtmp_url: e.target.value })}
                          focusBorderColor="pink.400" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Stream Key</FormLabel>
                        <Input type="password" value={form.rtmp_stream_key} placeholder="Stream key"
                          onChange={(e) => setForm({ ...form, rtmp_stream_key: e.target.value })}
                          focusBorderColor="pink.400" />
                      </FormControl>
                    </>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => { onClose(); setEditingRoom(null); }}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveRoom} isDisabled={!form.name}>
              {editingRoom ? 'Save Changes' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Join Token Modal */}
      <Modal isOpen={isTokenOpen} onClose={onTokenClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Join Link — {joinTokenRoom?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2} fontSize="sm" color="gray.500">This link expires in 15 minutes.</Text>
            <Box bg="gray.100" p={3} borderRadius="md" fontFamily="mono" fontSize="xs" wordBreak="break-all">
              {generatedToken}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => { navigator.clipboard.writeText(generatedToken); toast({ title: 'Copied!', status: 'success', duration: 1500 }); }} colorScheme="teal" mr={3}>
              Copy Link
            </Button>
            <Button variant="ghost" onClick={onTokenClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
